import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const ESTADO_CONFIG = {
  PENDIENTE: {
    label: "Pendiente",
    icon: "mdi:clock-outline",
    badgeClass: "classroom-badge-pendiente",
    color: "#d97706",
    description: "El alumno aún no ha entregado esta actividad.",
  },
  ENTREGADA: {
    label: "Entregada",
    icon: "mdi:check-circle-outline",
    badgeClass: "classroom-badge-entregada",
    color: "#16a34a",
    description: "El alumno entregó, esperando revisión del docente.",
  },
  VALIDADA: {
    label: "Validada",
    icon: "mdi:check-decagram",
    badgeClass: "classroom-badge-validada",
    color: "#059669",
    description: "El docente revisó y validó la entrega.",
  },
  VENCIDA: {
    label: "Vencida",
    icon: "mdi:alert-circle-outline",
    badgeClass: "classroom-badge-vencida",
    color: "#dc2626",
    description: "La fecha límite venció sin que el alumno entregara.",
  },
};

const ActividadDetailModal = ({
  show,
  onHide,
  actividad,
  loading = false,
  canManage = false,
  canSubmit = false,
  canReview = false,
  onEdit,
  onDelete,
  onEntregar,
  onRevisar,
  onDeleteArchivo,
  deletingArchivoId = null,
  formatDate = (d) => d,
  formatDateTime = (d) => d,
}) => {
  if (!show) return null;
  if (!actividad) return null;

  const estadoCfg = ESTADO_CONFIG[actividad.estado] || ESTADO_CONFIG.PENDIENTE;

  const isPending = actividad.estado === "PENDIENTE";
  const isEntregada = actividad.estado === "ENTREGADA";
  const isValidada = actividad.estado === "VALIDADA";
  const isVencida = actividad.estado === "VENCIDA";
  const tieneEntrega = !!(isEntregada || isValidada);
  // El alumno puede eliminar archivos de una entrega anterior (rechazada → PENDIENTE)
  const canDeleteFiles =
    canSubmit &&
    isPending &&
    actividad.entrega?.archivos &&
    actividad.entrega.archivos.length > 0;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content radius-16 bg-base border-0 shadow-lg">
          {/* Header con gradiente del estado */}
          <div
            className="modal-header py-20 px-24 border-0"
            style={{
              background: `linear-gradient(135deg, ${estadoCfg.color}12, ${estadoCfg.color}06)`,
              borderBottom: `3px solid ${estadoCfg.color}30`,
            }}
          >
            <div className="d-flex align-items-center gap-12">
              <div
                className="w-44-px h-44-px d-flex align-items-center justify-content-center radius-10"
                style={{ backgroundColor: `${estadoCfg.color}20` }}
              >
                <Icon
                  icon={estadoCfg.icon}
                  style={{ color: estadoCfg.color, fontSize: "22px" }}
                />
              </div>
              <div>
                <h5 className="modal-title fw-semibold mb-2">{actividad.titulo}</h5>
                <span
                  className={`text-xs fw-medium px-8 py-2 radius-4 d-inline-flex align-items-center gap-4 ${estadoCfg.badgeClass}`}
                >
                  <Icon icon={estadoCfg.icon} className="text-xs" />
                  {estadoCfg.label}
                </span>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onHide} />
          </div>

          {/* Body */}
          <div className="modal-body px-24 py-20">
            {loading ? (
              <div className="text-center py-40">
                <div className="spinner-border text-primary mb-12" role="status" />
                <p className="text-secondary-light text-sm mb-0">Cargando detalles...</p>
              </div>
            ) : (
              <>
            {/* Timeline de estado */}
            <div className="classroom-timeline mb-24">
              <div className="classroom-timeline-item">
                <div className="classroom-timeline-dot classroom-timeline-dot--completed" />
                <div>
                  <span className="fw-medium text-sm">Actividad creada</span>
                  <span className="text-secondary-light text-xs d-block">
                    {formatDateTime(actividad.created_at)}
                  </span>
                </div>
              </div>

              {tieneEntrega && (
                <>
                  <div className="classroom-timeline-item">
                    <div className="classroom-timeline-dot classroom-timeline-dot--completed" />
                    <div>
                      <span className="fw-medium text-sm">
                        {isEntregada ? "Entregada por el alumno" : "Entregada por el alumno"}
                      </span>
                      <span className="text-secondary-light text-xs d-block">
                        {actividad.entrega?.fecha_entrega_real
                          ? formatDateTime(actividad.entrega.fecha_entrega_real)
                          : "—"}
                      </span>
                    </div>
                  </div>
                  {isValidada && (
                    <div className="classroom-timeline-item">
                      <div className="classroom-timeline-dot classroom-timeline-dot--completed" />
                      <div>
                        <span className="fw-medium text-sm">Validada por el docente</span>
                        <span className="text-secondary-light text-xs d-block">
                          {actividad.entrega?.fecha_revision
                            ? formatDateTime(actividad.entrega.fecha_revision)
                            : "—"}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {isVencida && (
                <div className="classroom-timeline-item">
                  <div className="classroom-timeline-dot" style={{ borderColor: "#dc2626" }} />
                  <div>
                    <span className="fw-medium text-sm text-danger">Fecha límite vencida</span>
                    <span className="text-secondary-light text-xs d-block">
                      {formatDateTime(actividad.fecha_entrega)}
                    </span>
                  </div>
                </div>
              )}

              {isPending && (
                <div className="classroom-timeline-item">
                  <div className="classroom-timeline-dot" />
                  <div>
                    <span className="fw-medium text-sm">Esperando entrega</span>
                    <span className="text-secondary-light text-xs d-block">
                      Entregar antes de: {formatDateTime(actividad.fecha_entrega)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Detalles */}
            <div className="row g-16 mb-20">
              <div className="col-md-6">
                <span className="classroom-detail-label">Entregar antes de </span>
                <div className="d-flex align-items-center gap-8">
                  <Icon
                    icon="mdi:calendar-month"
                    style={{ color: "#64748b", fontSize: "18px" }}
                  />
                  <span className="fw-medium text-sm">
                    {formatDateTime(actividad.fecha_entrega)}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <span className="classroom-detail-label">Creado</span>
                <div className="d-flex align-items-center gap-8">
                  <Icon
                    icon="mdi:clock-outline"
                    style={{ color: "#64748b", fontSize: "18px" }}
                  />
                  <span className="fw-medium text-sm">
                    {formatDate(actividad.created_at)}
                  </span>
                </div>
              </div>

              {actividad.docente_nombre && (
                <div className="col-md-6">
                  <span className="classroom-detail-label">Docente</span>
                  <div className="d-flex align-items-center gap-8">
                    <Icon
                      icon="mdi:account-tie"
                      style={{ color: "#64748b", fontSize: "18px" }}
                    />
                    <span className="fw-medium text-sm">{actividad.docente_nombre}</span>
                  </div>
                </div>
              )}

              {actividad.alumno_nombre && (
                <div className="col-md-6">
                  <span className="classroom-detail-label">Alumno</span>
                  <div className="d-flex align-items-center gap-8">
                    <Icon
                      icon="mdi:account-school"
                      style={{ color: "#64748b", fontSize: "18px" }}
                    />
                    <span className="fw-medium text-sm">{actividad.alumno_nombre}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Descripción */}
            {actividad.descripcion && (
              <div className="mb-20">
                <span className="classroom-detail-label">Descripción</span>
                <div
                  className="p-16 radius-10 mt-4 ql-editor-content"
                  style={{ backgroundColor: "#f8fafc" }}
                  dangerouslySetInnerHTML={{ __html: actividad.descripcion }}
                />
              </div>
            )}

            {/* Comentario del alumno (si existe) */}
            {actividad.entrega?.comentario_alumno && (
              <div className="mb-20">
                <span className="classroom-detail-label">Comentario del alumno</span>
                <div
                  className="p-16 radius-10 mt-4 d-flex gap-12"
                  style={{ backgroundColor: "#eff6ff" }}
                >
                  <Icon
                    icon="mdi:comment-text-outline"
                    style={{ color: "#3b82f6", fontSize: "18px", flexShrink: 0, marginTop: "2px" }}
                  />
                  <p className="text-sm mb-0">{actividad.entrega.comentario_alumno}</p>
                </div>
              </div>
            )}

            {/* Comentario del docente (si existe) */}
            {actividad.entrega?.comentario_docente && (
              <div className="mb-20">
                <span className="classroom-detail-label">Comentario del docente</span>
                <div
                  className="p-16 radius-10 mt-4 d-flex gap-12"
                  style={{ backgroundColor: "#f0fdf4" }}
                >
                  <Icon
                    icon="mdi:comment-check-outline"
                    style={{ color: "#16a34a", fontSize: "18px", flexShrink: 0, marginTop: "2px" }}
                  />
                  <p className="text-sm mb-0">{actividad.entrega.comentario_docente}</p>
                </div>
              </div>
            )}

            {/* Archivos de la entrega (alumno) */}
            {actividad.entrega?.archivos && actividad.entrega.archivos.length > 0 && (
              <div className="mb-20">
                <div className="d-flex align-items-center justify-content-between mb-8">
                  <span className="classroom-detail-label mb-0">
                    Archivos entregados
                    {canDeleteFiles && (
                      <span className="text-secondary-light fw-normal ms-4 text-xs">
                        — puedes eliminarlos antes de re-entregar
                      </span>
                    )}
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-8 mt-8">
                  {actividad.entrega.archivos.map((archivo, idx) => {
                    const isDeleting = deletingArchivoId === archivo.id;
                    return (
                      <div
                        key={archivo.id || idx}
                        className="classroom-file-item text-decoration-none d-flex align-items-center gap-2"
                        style={{ borderLeft: "3px solid #22c55e" }}
                      >
                        <a
                          href={archivo.archivo || ""}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center gap-6 text-decoration-none flex-grow-1"
                        >
                          <Icon icon="mdi:file-document-outline" style={{ color: "#16a34a", flexShrink: 0 }} />
                          <span className="text-sm text-secondary text-truncate" style={{ maxWidth: "160px" }}>
                            {archivo.nombre_original || `Archivo ${idx + 1}`}
                          </span>
                          <Icon icon="mdi:download" style={{ color: "#3b82f6", fontSize: "16px", flexShrink: 0 }} />
                        </a>
                        {canDeleteFiles && onDeleteArchivo && (
                          <button
                            type="button"
                            className="btn p-0 d-flex align-items-center text-danger flex-shrink-0"
                            style={{ lineHeight: 1 }}
                            onClick={() => onDeleteArchivo(archivo.id)}
                            disabled={isDeleting}
                            title="Eliminar archivo"
                          >
                            {isDeleting ? (
                              <span className="spinner-border spinner-border-sm" style={{ width: "14px", height: "14px" }} />
                            ) : (
                              <Icon icon="mdi:close-circle" style={{ fontSize: "18px" }} />
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Archivos adjuntos del docente (si existen) */}
            {actividad.archivos && actividad.archivos.length > 0 && (
              <div className="mb-20">
                <span className="classroom-detail-label">Archivos adjuntos del docente</span>
                <div className="d-flex flex-wrap gap-8 mt-8">
                  {actividad.archivos.map((archivo, idx) => (
                    <a
                      key={idx}
                      href={archivo.url || archivo.archivo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="classroom-file-item text-decoration-none"
                    >
                      <Icon icon="mdi:file-document-outline" style={{ color: "#64748b" }} />
                      <span className="text-sm text-secondary">
                        {archivo.nombre || `Archivo ${idx + 1}`}
                      </span>
                      <Icon icon="mdi:download" style={{ color: "#3b82f6", fontSize: "16px" }} />
                    </a>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Footer con acciones según rol */}
          <div className="modal-footer py-16 px-24 border-0">
            <div className="d-flex justify-content-between w-100 flex-wrap gap-8">
              {/* Acciones izquierda: Gestionar (mentor) */}
              <div className="d-flex gap-8">
                {canManage && !isValidada && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary d-flex align-items-center gap-6 radius-10 px-14 py-8"
                      onClick={onEdit}
                    >
                      <Icon icon="mdi:pencil-outline" style={{ fontSize: "16px" }} />
                      <span>Editar</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger d-flex align-items-center gap-6 radius-10 px-14 py-8"
                      onClick={onDelete}
                    >
                      <Icon icon="mdi:trash-can-outline" style={{ fontSize: "16px" }} />
                      <span>Eliminar</span>
                    </button>
                  </>
                )}
                {canManage && isValidada && (
                  <span className="text-success fw-medium text-sm d-flex align-items-center gap-4">
                    <Icon icon="mdi:lock-outline" style={{ fontSize: "16px" }} />
                    Actividad validada — no editable
                  </span>
                )}
              </div>

              {/* Acciones derecha: Entregar / Revisar */}
              <div className="d-flex gap-8">
                {/* Botón entregar (alumno, sin entrega aún) */}
                {canSubmit && isPending && (
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-6 radius-10 px-16 py-8"
                    onClick={onEntregar}
                  >
                    <Icon icon="mdi:cloud-upload-outline" style={{ fontSize: "18px" }} />
                    <span>Entregar</span>
                  </button>
                )}

                {/* Entregado, esperando revisión (alumno) */}
                {canSubmit && isEntregada && (
                  <span className="d-flex align-items-center gap-6 text-primary fw-medium text-sm px-8">
                    <Icon icon="mdi:clock-outline" />
                    Entregado — esperando revisión
                  </span>
                )}

                {/* Botón revisar (docente, el alumno ya entregó) */}
                {canReview && isEntregada && (
                  <button
                    type="button"
                    className="btn btn-success d-flex align-items-center gap-6 radius-10 px-16 py-8"
                    onClick={onRevisar}
                  >
                    <Icon icon="mdi:check-decagram-outline" style={{ fontSize: "18px" }} />
                    <span>Revisar</span>
                  </button>
                )}

                {/* Sin entrega (docente ve aviso) */}
                {canReview && isPending && (
                  <span className="d-flex align-items-center gap-6 text-secondary fw-medium text-sm px-8">
                    <Icon icon="mdi:clock-outline" />
                    Aún no entrega
                  </span>
                )}

                {/* Validada */}
                {isValidada && (
                  <span className="d-flex align-items-center gap-6 text-success fw-medium text-sm px-8">
                    <Icon icon="mdi:check-decagram" />
                    Actividad validada
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActividadDetailModal;
